import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * NOTE ON SCOPE: the source script calls
 *   Fancybox.show([{ src: '#modal-contact', type: 'inline' }])
 * for every `[data-contact-modal]` button (in the header AND in the
 * contact section), and shows `#modal-thx` after a successful form
 * submit — but neither `#modal-contact` nor `#modal-thx` markup exists
 * anywhere in the uploaded index.html. That markup is missing from the
 * source, not just left for us to find.
 *
 * Rather than depend on the Fancybox library for an assumed modal we
 * can't verify, this replaces it with a small React context: any button
 * can call `openModal()`, and <ContactModal/> (mounted once, near the
 * root) renders based on that state. Swap in Fancybox later if you'd
 * rather match the original library exactly.
 */
const ModalContext = createContext(null);

export function ModalProvider({ children }) {
  const [status, setStatus] = useState('closed'); // 'closed' | 'form' | 'thanks'

  const openModal = useCallback(() => setStatus('form'), []);
  const closeModal = useCallback(() => setStatus('closed'), []);
  const showThanks = useCallback(() => setStatus('thanks'), []);

  return (
    <ModalContext.Provider value={{ status, openModal, closeModal, showThanks }}>
      {children}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModal must be used within a <ModalProvider>');
  return ctx;
}
